import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import CountUp from "react-countup";
import { RevenueCharts } from "./DashboardEcommerceCharts";
import DashboardService from "../../../../services/OrderingServices/DashboardService";

const Revenue = () => {
  const [chartData, setChartData] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("3years");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const currentYear = new Date().getFullYear();
  const [availableYears] = useState(
    Array.from({ length: 6 }, (_, i) => currentYear - i)
  );
  const [filteredData, setFilteredData] = useState([]);
  const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false);
  const [summaryStats, setSummaryStats] = useState({
    orders: 0,
    earnings: 0,
  
  });
  const [allYearsData, setAllYearsData] = useState({}); // Store data for all years
  const [loading, setLoading] = useState(false);

  const fetchDataForYear = async (year) => {
    try {
      setLoading(true);
      const response = await DashboardService.getReport(year);
      setLoading(false);
      return response;
    } catch (error) {
      console.log(`Error fetching data for year ${year}:`, error);
      setLoading(false);
      return [];
    }
  };

  const fetchInitialData = async () => {
    // Fetch data for the default selected year (2024)
    const yearData = await fetchDataForYear(selectedYear);

    setAllYearsData({ [selectedYear]: yearData });
    setChartData(yearData);
    setFilteredData(yearData);

    // Calculate summary statistics
    const totalSales = yearData.reduce((sum, item) => sum + item.totalSales, 0);
    setSummaryStats({
      orders: Math.floor(totalSales / 21.45),
      earnings: totalSales / 1000,
     
    });
  };

  const handleYearChange = async (year) => {
    setSelectedYear(year);
    setSelectedPeriod("year"); // Set to indicate year-based filtering

    // Check if we already have data for this year
    if (allYearsData[year]) {
      // Use cached data
      setFilteredData(allYearsData[year]);

      // Update summary stats
      const totalSales = allYearsData[year].reduce(
        (sum, item) => sum + item.totalSales,
        0
      );
      setSummaryStats({
        orders: Math.floor(totalSales / 21.45),
        earnings: totalSales / 1000,
        refunds: Math.floor(totalSales * 0.02),
        conversionRatio: 18.92,
      });
    } else {
      // Fetch new data for this year
      const yearData = await fetchDataForYear(year);

      // Update state with new data
      setAllYearsData((prev) => ({
        ...prev,
        [year]: yearData,
      }));

      setFilteredData(yearData);

      // Update summary stats
      const totalSales = yearData.reduce(
        (sum, item) => sum + item.totalSales,
        0
      );
      setSummaryStats({
        orders: Math.floor(totalSales / 21.45),
        earnings: totalSales / 1000,
        refunds: Math.floor(totalSales * 0.02),
        conversionRatio: 18.92,
      });
    }
  };

  const filterDataByPeriod = (period) => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    let filtered = [];

    setFilteredData(filtered);

    // Update summary stats based on filtered data
    const totalSales = filtered.reduce((sum, item) => sum + item.totalSales, 0);
    setSummaryStats({
      orders: Math.floor(totalSales / 21.45),
      earnings: totalSales / 1000,
      refunds: Math.floor(totalSales * 0.02),
      conversionRatio: 18.92,
    });
  };

  useEffect(() => {
    fetchInitialData(); // Initialize with selected year data
  }, []);

  const onChangeChartPeriod = async (period) => {
    setSelectedPeriod(period);

    // Check if it's a specific year
    const year = parseInt(period);
    if (!isNaN(year)) {
      setSelectedYear(year);

      // If we don't have data for this year, fetch it
      if (!allYearsData[year]) {
        const yearData = await fetchDataForYear(year);
        setAllYearsData((prev) => ({
          ...prev,
          [year]: yearData,
        }));

        setFilteredData(yearData);

        // Update summary stats
        const totalSales = yearData.reduce(
          (sum, item) => sum + item.totalSales,
          0
        );
        setSummaryStats({
          orders: Math.floor(totalSales / 21.45),
          earnings: totalSales / 1000,
          refunds: Math.floor(totalSales * 0.02),
          conversionRatio: 18.92,
        });
      } else {
        // Use existing data
        filterDataByPeriod(period);
      }
    } else {
      // For period selections (1year, 2years, etc.)
      filterDataByPeriod(period);
    }
  };

  return (
    <React.Fragment>
      <Card className="h-100">
        <CardHeader className="border-0 align-items-center d-flex">
          <h4 className="card-title mb-0 flex-grow-1">Revenue</h4>
          <div className="d-flex gap-2 align-items-center">
            {/* Year Dropdown */}
            <Dropdown
              isOpen={isYearDropdownOpen}
              toggle={() => setIsYearDropdownOpen(!isYearDropdownOpen)}
            >
              <DropdownToggle
                className="btn btn-primary btn-sm text-white"
                caret
                disabled={loading}
              >
                {loading ? "Loading..." : `Year: ${selectedYear}`}
              </DropdownToggle>
              <DropdownMenu>
                {availableYears.map((year) => (
                  <DropdownItem
                    key={year}
                    active={selectedYear === year}
                    onClick={() => handleYearChange(year)}
                  >
                    {year}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
          </div>
        </CardHeader>

        <CardHeader className="p-0 border-0 bg-light-subtle">
          <Row className="g-0 text-center">
            <Col xs={6} sm={3}>
              <div className="p-3 border border-dashed border-start-0">
                <h5 className="mb-1">
                  <CountUp
                    start={0}
                    end={summaryStats.orders}
                    duration={3}
                    separator=","
                  />
                </h5>
                <p className="text-muted mb-0">Orders</p>
              </div>
            </Col>
            <Col xs={6} sm={3}>
              <div className="p-3 border border-dashed border-start-0">
                <h5 className="mb-1">
                  <CountUp
                    start={0}
                    decimals={2}
                    end={summaryStats.earnings}
                    duration={3}
                    suffix="k"
                  />
                </h5>
                <p className="text-muted mb-0">Earnings</p>
              </div>
            </Col>
            {/* <Col xs={6} sm={3}>
              <div className="p-3 border border-dashed border-start-0">
                <h5 className="mb-1">
                  <CountUp start={0} end={summaryStats.refunds} duration={3} />
                </h5>
                <p className="text-muted mb-0">Refunds</p>
              </div>
            </Col> */}
            {/* <Col xs={6} sm={3}>
              <div className="p-3 border border-dashed border-start-0 border-end-0">
                <h5 className="mb-1 text-success">
                  <CountUp
                    start={0}
                    end={summaryStats.conversionRatio}
                    decimals={2}
                    duration={3}
                    suffix="%"
                  />
                </h5>
                <p className="text-muted mb-0">Conversion Ratio</p>
              </div>
            </Col> */}
          </Row>
        </CardHeader>

        <CardBody className="p-0 pb-2">
          <div className="w-100">
            <div dir="ltr">
              <RevenueCharts
                series={filteredData}
                dataColors='["--vz-success", "--vz-primary", "--vz-danger"]'
              />
            </div>
          </div>
        </CardBody>
      </Card>
    </React.Fragment>
  );
};

export default Revenue;
